import { FC, useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useMutation } from 'react-query';
import { useStore } from 'hooks/useRootStore';
import { useAppPreloader } from 'hooks/useAppPreloader';
import { useHistory } from 'react-router';
import { useStyles } from './styles';
import { ChangePasswordFormInput, ChangePasswordRequest, ResetPasswordFormProps } from './types';
import { getPath } from './paths';

import {
  Grid,
  FormControl,
  Button,
  InputLabel,
  Input,
  InputAdornment,
  IconButton,
  FormHelperText,
  Paper,
  Typography,
} from '@material-ui/core';

import { observer } from 'mobx-react-lite';
import Visibility from '@material-ui/icons/Visibility';
import VisibilityOff from '@material-ui/icons/VisibilityOff';
import axios from 'server';
import { getUrlSearchValue } from 'utils/urlUtils';
import { handleCompanyUrl, handleOrganizationUrl } from 'utils/organizationUtils';
import { useParams } from 'react-router-dom';
import { OrganizationParamUrl } from 'containers/SignInEndUser/types';
export const ResetPasswordForm: FC<ResetPasswordFormProps> = observer(({ userType }) => {
  const classes = useStyles();
  const history = useHistory();
  // const url = useLocation();
  const [expiredToken, setEpiredToken] = useState(false);
  // const UrlExtracted = url.pathname.split('/')[1];
  const { domain } = useParams<OrganizationParamUrl>();
  const {
    ldapStore: { validateTokens },
    notificationStore: { openNotification },
    appPreloaderStore: { disablePreloader },
  } = useStore();
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showPasswords, setShowPasswords] = useState<boolean>(false);
  const changePasswordReq = useMutation((data: ChangePasswordRequest) => axios.post(getPath(userType), data), {
    mutationKey: 'changeAdminPasswordReq',
    onSuccess: () => {
      openNotification('success', `Password changed successfully`);
      history.push(domain ? handleOrganizationUrl(domain, '/login') : handleCompanyUrl('/login'));
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.data?.message || 'Something went wrong while changing the password.';
      openNotification('error', errorMessage);
    },
  });
  const tokenValidateReq = useMutation(
    () => {
      const token = getUrlSearchValue('token');
      const email = getUrlSearchValue('email');
      return validateTokens(token, email);
    },
    {
      mutationKey: 'tokenValidateReq',
      onSuccess: () => {
        disablePreloader();
      },
      onError: ({ message }: Error) => {
        setEpiredToken(true);
        openNotification('error', message);
      },
    }
  );

    useEffect(() => {
      tokenValidateReq.mutate();

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

  const { control, handleSubmit, watch } = useForm<ChangePasswordFormInput>();
  const passwordValue = watch('password');

  const onSubmit = ({ password, passwordConfirm }: ChangePasswordFormInput) => {
    changePasswordReq.mutate({
      password,
      password_confirmation: passwordConfirm,
      token: getUrlSearchValue('token'),
      email: getUrlSearchValue('email'),
    });
  };

    useAppPreloader(
      changePasswordReq.isLoading ||
        tokenValidateReq.isLoading
    );

    /*
     * Loading
     */
    if (tokenValidateReq.isLoading) {
      return null;
    }

    /*
     * Expired token
     */
    if (expiredToken) {
      return (
        <Grid
          container
          className={classes.mainError}
          justify="center"
          alignItems="center"
        >
          <Grid item xs={12} sm={10} md={7} lg={6}>
            <Typography
              className={classes.title}
              variant="h5"
              align="center"
            >
              YOU HAVE ALREADY UPDATED YOUR PASSWORD USING THIS LINK.
            </Typography>
          </Grid>
        </Grid>
      );
    }

    return (
      <Grid
        container
        className={classes.main}
        justify="center"
        alignItems="center"
      >
        <Grid
          item
          xs={12}
          sm={10}
          md={7}
          lg={5}
          xl={4}
        >
          <Paper
            className={classes.paper}
            elevation={3}
          >
            <Typography
              className={classes.title}
              variant="h5"
              component="h1"
            >
              Change Password
            </Typography>

            <form
              onSubmit={handleSubmit(onSubmit)}
              // className={classes.form}
            >
              {/* Password */}
              <Controller
                name="password"
                control={control}
                defaultValue=""
                rules={{
                  required: true,
                }}
                render={({
                  field,
                  fieldState: { invalid, error },
                }) => (
                  <FormControl
                    className={classes.formControl}
                    fullWidth
                  >
                    <InputLabel error={invalid}>
                      Password
                    </InputLabel>

                    <Input
                      {...field}
                      autoFocus
                      fullWidth
                      error={invalid}
                      type={
                        showPassword
                          ? 'text'
                          : 'password'
                      }
                      endAdornment={
                        <InputAdornment position="end">
                          <IconButton
                            edge="end"
                            aria-label="toggle password visibility"
                            onClick={() =>
                              setShowPassword(
                                (prev) => !prev
                              )
                            }
                          >
                            {showPassword ? (
                              <Visibility />
                            ) : (
                              <VisibilityOff />
                            )}
                          </IconButton>
                        </InputAdornment>
                      }
                    />

                    {invalid && (
                      <FormHelperText error>
                        {error?.type === 'validate'
                          ? 'Password and confirm password must be same'
                          : 'Required field'}
                      </FormHelperText>
                    )}
                  </FormControl>
                )}
              />

              {/* Confirm Password */}
              <Controller
                name="passwordConfirm"
                control={control}
                defaultValue=""
                rules={{
                  required: true,
                  validate: (value) =>
                    value === passwordValue,
                }}
                render={({
                  field,
                  fieldState: { invalid, error },
                }) => (
                  <FormControl
                    className={classes.formControl}
                    fullWidth
                  >
                    <InputLabel error={invalid}>
                      Confirm Password
                    </InputLabel>

                    <Input
                      {...field}
                      fullWidth
                      error={invalid}
                      type={
                        showPasswords
                          ? 'text'
                          : 'password'
                      }
                      endAdornment={
                        <InputAdornment position="end">
                          <IconButton
                            edge="end"
                            aria-label="toggle confirm password visibility"
                            onClick={() =>
                              setShowPasswords(
                                (prev) => !prev
                              )
                            }
                          >
                            {showPasswords ? (
                              <Visibility />
                            ) : (
                              <VisibilityOff />
                            )}
                          </IconButton>
                        </InputAdornment>
                      }
                    />

                    {invalid && (
                      <FormHelperText error>
                        {error?.type === 'validate'
                          ? 'Password and confirm password must be same'
                          : 'Required field'}
                      </FormHelperText>
                    )}
                  </FormControl>
                )}
              />

              {/* Submit */}
              <Button
                className={classes.submitBtn}
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                disabled={changePasswordReq.isLoading}
              >
                {changePasswordReq.isLoading
                  ? 'Submitting...'
                  : 'Submit'}
              </Button>
            </form>
          </Paper>
        </Grid>
      </Grid>
    );
  }
);